import React, { memo, useCallback, useEffect, useRef, useState } from "react";

// propTypes
import PropTypes from "prop-types";

import {
  convertCartToProductFormat,
  parseCssStringToObject,
  sentPostMessage,
} from "../../../../helper";
import {
  DEFAULT_BLOCK_CSS,
  POPUP_SECTIONS,
} from "../../../../constant/constant";
import { useApp } from "../../../../context/AppState";
import TextBlock from "./TextBlock";
import DraggableWrapper from "../../../Draggable";
import ImageSliderBlock from "./ImageSliderBlock";
import QuantityPickerBlock from "./QuantityPickerBlock";
import PriceBlock from "./PriceBlock";

import cartService from "../../../../services/cart.service";

function CartLoadingSpinner() {
  return (
    <span
      className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"
      aria-label="Loading cart"
      role="status"
    />
  );
}
const CartItemBlockNew = ({
  activeStep,
  popupData,
  popupState,
  setPopupState,
  section,
  index: parentIndex,
  handleClose,
}) => {
  const { appState, isMobile, cartData, setCartData } = useApp();

  const [selectedBlock, setSelectedBlock] = useState({});
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isEnd, setIsEnd] = useState(false);
  const [product, setProduct] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingLines, setLoadingLines] = useState([]);

  const fetchCart = async () => {
    setLoading(true);
    try {
      const data = await cartService.fetchCart();
      setCartData(data);
      const formattedProduct = convertCartToProductFormat(data);
      setProduct(formattedProduct);
    } catch (err) {
      console.error("Error fetching cart:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (window.isLiveModeTrigger === false) {
      setCartData(section?.products?.[0]);
      const formattedProduct = convertCartToProductFormat(section?.products?.[0]);
      setProduct(formattedProduct);
      setLoading(false);
    } else {
      fetchCart();
    }
  }, [appState?.is_preview_mode,section?.products]);


  const updateQuantity = async (productId, quantity, action) => {
    if (window.isLiveModeTrigger === false) {
      return;
    }
    const lineKey = `${productId}_${action}`;
    setLoadingLines((prev) => [...prev, lineKey]);
    try {
      const data = await cartService.changeQuantity(productId, quantity);
      setCartData(data);
      const formattedProduct = convertCartToProductFormat(data);
      setProduct(formattedProduct);
    } catch (err) {
      console.error("Error updating quantity:", err);
    } finally {
      setLoadingLines((prev) => prev.filter((l) => l !== lineKey));
    }
  };

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.on("slideChange", () => {
        setActiveIndex(swiperRef.current.activeIndex);
        setIsEnd(swiperRef.current.isEnd);
      });
    }
  }, []);

  const selectedBlockRef = useRef(selectedBlock);
  selectedBlockRef.current = selectedBlock;

  const handleBlockClick = useCallback(
    (type, subSectionIndex) => {
      if (!appState?.is_preview_mode) return;
      const prev = selectedBlockRef.current;
      if (prev.type === type && prev.index === subSectionIndex) return;
      sentPostMessage("select_cart_block", {
        index: parentIndex,
        subIndex: subSectionIndex,
      });
      window.parent?.postMessage(
        {
          event: "select_cart_block",
          type,
          index: parentIndex,
          subIndex: subSectionIndex,
        },
        "*"
      );
      setSelectedBlock({ type, index: subSectionIndex });
    },
    [appState?.is_preview_mode, parentIndex]
  );

  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || data.event !== "sectionCartCreated") return;
      const { type, subIndex: subSectionIndex } = data;
      if (typeof subSectionIndex === "number" && subSectionIndex > -1) {
        setSelectedBlock({ type, index: subSectionIndex });
        handleBlockClick(type, subSectionIndex);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [handleBlockClick]);

  const blockCSS = {
    ...DEFAULT_BLOCK_CSS,
    ...section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
    ...parseCssStringToObject(
      section?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
    ),
  };

  const productSectionsSwitch = useCallback(
    (productData, productIndex, productSection, productSectionIndex) => {

      switch (productSection?.type) {
        case POPUP_SECTIONS.TITLE:
          return (
            <TextBlock
              section={{ ...productSection, text: productData?.title }}
              parentData={productData}
            />
          );

        case POPUP_SECTIONS.COVER_IMAGE:
          return (
            <ImageSliderBlock
              section={productSection}
              productIndex={productIndex}
              productData={productData}
            />
          );

        case POPUP_SECTIONS.QUANTITY_PICKER:
          return (
            <QuantityPickerBlock
              section={productSection}
              index={productSectionIndex}
              productIndex={productIndex}
              productData={productData}
              isCustomCart={true}
              updateQuantity={updateQuantity}
              loadingLines={loadingLines}
              popupData={popupData}
              activeStep={activeStep}
            />
          );

        case POPUP_SECTIONS.PRICE:
          return (
            <PriceBlock
              productData={productData}
              section={productSection}
              index={productSectionIndex}
              productIndex={productIndex}
            />
          );
        default:
          return null;
      }
    },
    [popupData, popupState, setPopupState, section]
  );

  return (
    <>
      {loading ? (
        <div className="flex h-full items-center justify-center overflow-hidden">
          <CartLoadingSpinner />
        </div>
      ) : product?.length > 0 ? (
        <div className="h-full overflow-x-auto">
          {product?.map((productData, productIndex) => {
            return (
              <div style={blockCSS} key={productIndex}>
                {section?.sections?.map(
                  (productSection, productSectionIndex) => {
                    return (
                      <DraggableWrapper
                        key={`${parentIndex}-${productIndex}-${productSectionIndex}`}
                        index={parentIndex}
                        popupSettings={
                          popupData?.schema[activeStep]?.popupSettings
                        }
                        lockDraggable={
                          popupData?.generalSettings?.lockDraggable
                        }
                        section={productSection}
                        isSelected={
                          productSection?.type === selectedBlock?.type &&
                          productSectionIndex === selectedBlock?.index
                        }
                        onClick={() =>
                          handleBlockClick(
                            productSection?.type,
                            productSectionIndex,
                            productIndex
                          )
                        }
                        isSubSection
                        subIndex={productSectionIndex}
                        sectionLockDraggable={
                          productSection?.setting?.isLockDraggable
                        }
                      >
                        {productSectionsSwitch(
                          productData,
                          productIndex,
                          productSection,
                          productSectionIndex
                        )}
                      </DraggableWrapper>
                    );
                  }
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            ...blockCSS,
            textAlign: "center",
            padding: "20px",
            border: "none",
            backgroundColor: "transparent",
          }}
        >
          <p>No items in the cart.</p>
        </div>
      )}
    </>
  );
};

CartItemBlockNew.propTypes = {
  section: PropTypes.object,
};

export default CartItemBlockNew;
